import React, { useEffect, useState } from "react";
import { Button, showToast } from "../../../../../../../../components";
import { useDispatch, useSelector } from "react-redux";
import { formatCurrency } from "../../../../../../../../utils/stringUtils";
import { filterEmptyValues } from "../../../../../../../../utils/filterEmptyValues";
import { useLoginUser } from "../../../../../../../../hooks/useLoginUser";
import {
  setCurrentPayrollEditing,
  updatePayrollStart,
} from "../../../../../../../../redux/slices/payroll/payrollSlice";
import { label } from "framer-motion/client";
import common from "../../../../../../../../utils/common";
import dayjs from "dayjs";
import { calculateTotalAmount } from "../ProposedEmployeesDetails";
import axiosRequest from "../../../../../../../../redux/slices/universal/axiosService";
import { ENDPOINTS } from "../../../../../../../../redux/apiConfig";
function Row({ label, value, valueColor = "text-neutrals-900" }) {
  return (
    <div className="flex justify-between px-6 py-4">
      <span className="text-neutrals-900">{label}</span>
      <span className={`font-medium ${valueColor}`}>{value}</span>
    </div>
  );
}

const Section = ({ title, rows = [], headerClass = "", tag }) => (
  <div className="border rounded-t-md">
    <div className={`px-4 py-3 font-normal border rounded-t-md ${headerClass}`}>
      {title}
    </div>
    {tag && (
      <div className={`${tag.bg} px-4 py-2 text-sm ${tag.text}`}>
        {tag.label}
      </div>
    )}
    <div className="divide-y text-sm">
      {rows.map((row, idx) => (
        <Row key={idx} {...row} />
      ))}
    </div>
  </div>
);

const PayStub = ({ onHoldClick, hideButtons = false }) => {
  const {
    groupDeductions,
    updateDeductionsData,
    generateInstallmentUpdatePayloads,
  } = common;
  const [showFinalize, setShowFinalize] = useState(false);
  const dispatch = useDispatch();
  const { currentPayrollEditing } = useSelector((state) => state.payRoll) || {};
  const { userData: loginUserData } = useLoginUser();
  // const organizationId = loginUserData?.organizationId.uiid;
  const [earnings, setEarningsSections] = useState([]);
  const [deductions, setDeductionsSections] = useState([]);

  const employeeId = currentPayrollEditing?.employeeId?.uiid;
  const loginUserUiid = loginUserData?.uiid;

  useEffect(() => {
    dispatch(
      setCurrentPayrollEditing({
        tabswitched: true,
      }),
    );
  }, []);

  useEffect(() => {
    //displaying earnings config
    const timesheetsToDisplay =
      currentPayrollEditing?.timesheets?.map?.((data) => ({
        title: "Timesheets",
        headerClass: "bg-primary-100 text-gray-800",
        rows: [
          { label: "Client", value: data.clientId?.businessName || "-" },
          {
            label: "Job Title",
            value: data?.placementId?.jobInfo?.jobTitle || "-",
          },
          { label: "Pay Cycle", value: currentPayrollEditing?.payCycle },
          {
            label: "Classification",
            value: data?.classification || "Standard Time",
          },
          {
            label: "Total Hours",
            value: data?.dailyEntries?.reduce((a, b) => a + b.st, 0).toFixed(2),
          },
          {
            label: "Pay Rate",
            value: data?.amounts?.rateBreakdown?.map(a=>formatCurrency(a?.rate))?.filter(Boolean)?.join(','),
          },
          {
            label: "Total Amount",
            value: formatCurrency(data?.amounts?.deduction),
          },
        ],
      })) || [];
    const extraTimesheetsToDisplay =
      currentPayrollEditing?.unpaidtimesheets?.map?.((data) => ({
        title: "Extra Timesheets",
        headerClass: "bg-primary-100 text-gray-800",
        rows: [
          { label: "Client", value: data.clientId?.businessName || "-" },
          {
            label: "Job Title",
            value: data?.placementId?.jobInfo?.jobTitle || "-",
          },
          { label: "Pay Cycle", value: currentPayrollEditing?.payCycle },
          {
            label: "Classification",
            value: data?.classification || "Standard Time",
          },
          {
            label: "Total Hours",
            value: data?.dailyEntries?.reduce((a, b) => a + b.st, 0).toFixed(2),
          },
          {
            label: "Pay Rate",
            value: formatCurrency(
              data?.placementId?.payments.slice(-1)[0]?.payRate?.payRatePerHour,
            ),
          },
          {
            label: "Adjusted Amount",
            value: formatCurrency(
              data?.amounts?.adjustment?.find(
                (a) => a.uiid === currentPayrollEditing?.payrollId,
              )?.amount
                ? data?.amounts?.adjustment?.find(
                    (a) => a.uiid === currentPayrollEditing?.payrollId,
                  )?.amount
                : data?.amounts?.deduction ? data?.amounts?.deduction : data?.amounts?.total || 0,
            ),
          },
        ],
      })) || [];
    // const adjustmentsToDisplay = [
    //   {
    //     title: "Adjustments",
    //     headerClass: "bg-primary-100 text-gray-800",
    //     tag: {
    //       label: "Excess Payments",
    //       bg: "bg-success-100",
    //       text: "text-neutrals-900",
    //     },
    //     rows: [
    //       { label: "Client Name", value: "-" },
    //       { label: "Period", value: "-" },
    //       { label: "Difference", value: "-" },
    //     ],
    //   },
    // ];
    const adjustmentsToDisplay = (
      currentPayrollEditing?.excessDetails || []
    )?.map((data) => ({
      title: "Short Payments",
      tag: {
        label: "Excess Payments",
        bg: "bg-success-100",
        text: "text-neutrals-900",
      },
      headerClass: "bg-primary-100 text-gray-800",
      rows: [
        { label: "Client Name", value: data?.clientId?.businessName },
        {
          label: "Period",
          value: `${dayjs(data?.period?.from)
            .local()
            .format("MM/DD/YYYY")} - ${dayjs(data?.period?.to)
            .local()
            .format("MM/DD/YYYY")}`,
        },
        {
          label: "Difference",
          value: formatCurrency(
            Math.abs((data?.pay?.original || 0) - (data?.pay?.revised || 0)),
          ), // Typo here: "Lelf" -> "Left"?
          valueColor: "",
        },
      ],
    }));
    const hasOtherPayments =
      currentPayrollEditing?.otherPays?.morePay?.length > 0 ||
      currentPayrollEditing?.finalOtherTotalAmount > 0;
    const otherPaymentsToDisplay =hasOtherPayments ? [
      {
        title: "Other Payments",
        headerClass: "bg-primary-100 text-gray-800",
        rows: [
          {
            label: "Payroll Amount",
            value: currentPayrollEditing?.otherPays?.payrollAmt ?? 0,
          },
          // Spread the mapped array only if morePay exists
          ...(currentPayrollEditing?.otherPays?.morePay || []).map((data) => ({
            label: data?.type || "Unknown",
            value: data?.amount || 0,
          })),
          {
            label: "Bonus",
            value: currentPayrollEditing?.totalBonuses || 0,
          },
        ],
      },
    ] : [];
    
    const defaultEarningsSectionsWithoutUnpaidTSAdjustments = [
      ...timesheetsToDisplay,
      ...extraTimesheetsToDisplay,
      ...adjustmentsToDisplay,
      ...otherPaymentsToDisplay,
    ];
    const earningsSectionsWithUnpaidTSAdjustments = [
      ...timesheetsToDisplay,
      ...adjustmentsToDisplay,
      ...otherPaymentsToDisplay,
    ];
    setEarningsSections(
      defaultEarningsSectionsWithoutUnpaidTSAdjustments?.filter(Boolean) || [],
    );
    //displaying deductions config
    const deductionsToDisplay = [
      {
        title: "Deductions",
        headerClass: "bg-primary-100 text-gray-800",
        rows: [
          { label: "Deduction Type", value: "-" },
          { label: "Period", value: "-" },
          {
            label: "Deduction Amount",
            value: "-",
            valueColor: "text-danger-600",
          },
          {
            label: "Deduction Type",
            value: "-",
            valueColor: "text-danger-600",
          },
          {
            label: "Period",
            value: "-",
            valueColor: "text-primary-300",
          },
          {
            label: "Deduction Amount",
            value: "-",
            valueColor: "-",
          },
        ],
      },
    ];
    const reduxDeductions = (currentPayrollEditing?.deductions || []).map(
      (data) => ({
        title: "Deductions",
        headerClass: "bg-primary-100 text-gray-800",
        rows: [
          { label: "Deduction Type", value: data?.name },
          {
            label: "Period",
            value: `${dayjs(data?.payCycle?.from)
              .local()
              .format("MM/DD/YYYY")} - ${dayjs(data?.payCycle?.to)
              .local()
              .format("MM/DD/YYYY")}`,
          },

          {
            label: "Deduction Amount",
            value: formatCurrency(
              Number(data?.deductionAmountLelf) ||
                Number(data?.deductionAmountInput),
            ), // Typo here: "Lelf" -> "Left"?
            valueColor: "text-danger-600",
          },
        ],
      }),
    );

    // const shortPaymentsToDisplay = [
    //   {
    //     title: "Short Payments",
    //     headerClass: "bg-primary-100 text-gray-800",
    //     rows: [
    //       { label: "Client Name", value: "-" },
    //       { label: "Period", value: "-" },
    //       {
    //         label: "Difference",
    //         value: "-",
    //         valueColor: "text-danger-600",
    //       },
    //     ],
    //   },
    // ];
    const shortPaymentsToDisplay = (
      currentPayrollEditing?.shortDetails || []
    )?.map((data) => ({
      title: "Short Payments",
      headerClass: "bg-primary-100 text-gray-800",
      rows: [
        { label: "Client Name", value: data?.clientId?.businessName },
        {
          label: "Period",
          value: `${dayjs(data?.period?.from)
            .local()
            .format("MM/DD/YYYY")} - ${dayjs(data?.period?.to)
            .local()
            .format("MM/DD/YYYY")}`,
        },
        {
          label: "Difference",
          value: formatCurrency(data?.pay?.original - data?.pay?.revised), // Typo here: "Lelf" -> "Left"?
          valueColor: "text-danger-600",
        },
      ],
    }));
    setDeductionsSections([...reduxDeductions, ...shortPaymentsToDisplay]);
  }, [currentPayrollEditing]);

  // const handleSaveOld = async (status) => {
  //   try {
  //     if (status === 2 && !calculateTotalAmount(currentPayrollEditing)) {
  //       showToast("Cannot Finalize without any amount", "info", 1000);
  //       return;
  //     }
  //     //////Adjustments to change overflow and underflow logic for the deductions and
  //     ///// update the deduction document for installments for the adjusted values for installments
  //     let adjustments = [];

  //     currentPayrollEditing?.deductions?.forEach((item) => {
  //       const enteredAmount = Number(item?.deductionAmountLelf || 0);
  //       if (enteredAmount > 0) {
  //         adjustments.push({
  //           uiid: item?.uiid,
  //           amount: enteredAmount,
  //           installmentId: item?._id,
  //         });
  //       }
  //     });

  //     if (adjustments.length > 0) {
  //       const round = (n) => Number(Number(n || 0).toFixed(2));

  //       const promises = adjustments.map(async (adj) => {
  //         const res = await axiosRequest({
  //           method: "GET",
  //           url: ENDPOINTS.EMPLOYEE_DEDUCTIONS.GET_BY_UIID,
  //           params: {
  //             coll: "EmpDeduction",
  //             fil: JSON.stringify({ uiid: { type: "NONE", value: adj.uiid } }),
  //           },
  //         });

  //         const deductionDoc = res?.data?.data;
  //         if (!deductionDoc)
  //           throw new Error(`Deduction not found for uiid ${adj.uiid}`);

  //         const desiredTotal = round(Number(adj.amount || 0));
  //         let installments = (deductionDoc.installments || []).map((i) => ({
  //           ...i,
  //           receivable: round(Math.max(0, Number(i?.receivable || 0))),
  //           received: round(Math.max(0, Number(i?.received || 0))),
  //         }));

  //         const currentIndex = installments.findIndex(
  //           (i) => i._id?.toString() === adj.installmentId?.toString(),
  //         );

  //         if (currentIndex !== -1) {
  //           const inst = installments[currentIndex];
  //           const oldTotal = inst.received + inst.receivable;

  //           // set current installment directly to entered
  //           inst.received = desiredTotal;
  //           inst.receivable = 0;

  //           let diff = desiredTotal - oldTotal;

  //           if (diff > 0) {
  //             // overflow → take from subsequent installments’ receivable
  //             for (
  //               let i = currentIndex + 1;
  //               i < installments.length && diff > 0;
  //               i++
  //             ) {
  //               let nxt = installments[i];
  //               let reduceHere = Math.min(nxt.receivable, diff);
  //               nxt.receivable = round(nxt.receivable - reduceHere);
  //               diff = round(diff - reduceHere);
  //             }
  //           } else if (diff < 0) {
  //             // underflow → add back to subsequent installments’ receivable
  //             let rollback = Math.abs(diff);
  //             for (
  //               let i = currentIndex + 1;
  //               i < installments.length && rollback > 0;
  //               i++
  //             ) {
  //               installments[i].receivable = round(
  //                 installments[i].receivable + rollback,
  //               );
  //               rollback = 0;
  //             }
  //           }
  //         }

  //         const totalReceivedAfter = round(
  //           installments.reduce((sum, i) => sum + i.received, 0),
  //         );

  //         const updatePayload = {
  //           payload: [
  //             {
  //               type: "SET",
  //               value: {
  //                 _skipFlatten: ["installments"],
  //                 installments: installments.map((it) => ({
  //                   ...it,
  //                   received: round(it.received),
  //                   receivable: round(it.receivable),
  //                 })),
  //                 amounts: {
  //                   ...deductionDoc.amounts,
  //                   received: totalReceivedAfter,
  //                 },
  //               },
  //             },
  //           ],
  //         };
  //         const updateResponse = await axiosRequest({
  //           method: "PATCH",
  //           url: ENDPOINTS.EMPLOYEE_DEDUCTIONS.UPDATE,
  //           params: { coll: "EmpDeduction", uiid: adj.uiid },
  //           data: updatePayload,
  //         });

  //         return {
  //           uiid: adj.uiid,
  //           updatedDoc: updateResponse?.data || deductionDoc,
  //         };
  //       });

  //       try {
  //         const results = await Promise.all(promises);
  //         // showToast("All adjustments processed successfully", "success");
  //       } catch (err) {
  //         console.error("Error applying deductions", err);
  //         showToast("Something went wrong", "error");
  //         return;
  //       }
  //     }
  //     /////////////////////////////////////////////

  //     const allTimesheetIds = [
  //       ...(Array.isArray(currentPayrollEditing?.timesheets)
  //         ? currentPayrollEditing?.timesheets
  //         : []),
  //       ...(Array.isArray(currentPayrollEditing?.unpaidtimesheets)
  //         ? currentPayrollEditing?.unpaidtimesheets
  //         : []),
  //     ];

  //     const deductionsResult =
  //       groupDeductions(currentPayrollEditing?.deductions ?? []) || {};

  //     const dataDeduction = generateInstallmentUpdatePayloads(
  //       currentPayrollEditing?.deductions,
  //       currentPayrollEditing?.payrollId,
  //     );

  //     const UnpaidAdjustmentPayload =
  //       currentPayrollEditing?.unpaidtimesheets?.map((item) => ({
  //         timesheetId: item?.uiid,
  //         deduction: item?.amounts?.deduction || 0,
  //       })) || [];
  //     let createdPayload = [
  //       {
  //         type: "SET",
  //         value: {
  //           _skipFlatten: [
  //             "timesheets.ids",
  //             "otherPays.morePay",
  //             "deductions.insurance.installmentId",
  //             "deductions.regular",
  //             "bonuses.ids",
  //             "adjustments",
  //           ],
  //           timesheets: {
  //             totalHrs: currentPayrollEditing?.totalTimesheetHours || 0,
  //             totalPay: currentPayrollEditing?.totalTimesheetPayAmount || 0,
  //             // ids: allTimesheetIds?.map((data) => data.uiid) || [],
  //             ids:
  //               currentPayrollEditing?.timesheets?.map((data) => data.uiid) ||
  //               [],
  //             // currentTSIDs: currentPayrollEditing?.timesheets?.map((data) => data.uiid) || [],
  //             unpaidIds:
  //               currentPayrollEditing?.unpaidtimesheets?.map(
  //                 (data) => data.uiid,
  //               ) || [],
  //             //temporaray
  //             ...(currentPayrollEditing?.modifications?.type && {
  //               modifications: {
  //                 type: currentPayrollEditing?.modifications?.type,
  //                 amt: currentPayrollEditing?.modifications?.amt,
  //               },
  //             }),
  //             totalUnpaid: currentPayrollEditing?.modifications?.amt
  //               ? currentPayrollEditing?.modifications?.amt
  //               : currentPayrollEditing?.modifications?.amt === 0
  //                 ? 0
  //                 : currentPayrollEditing?.totalUnpaidPayAmount || 0,
  //           },
  //           bonuses: {
  //             totalPay: currentPayrollEditing?.totalBonuses || 0,
  //             ids: currentPayrollEditing?.bonusIds || [],
  //           },
  //           status,
  //           adjustments: {
  //             short: {
  //               ids: currentPayrollEditing?.shortIds || [],
  //               amt: currentPayrollEditing?.totalShort || 0,
  //             },
  //             excess: {
  //               ids: currentPayrollEditing?.excessIds || [],
  //               amt: currentPayrollEditing?.totalExcess || 0,
  //             },
  //           },
  //           deductions: deductionsResult,

  //           ...(status === 2
  //             ? {
  //                 finalized: {
  //                   by: loginUserUiid,
  //                   on: new Date().toISOString(),
  //                 },
  //               }
  //             : {}),
  //           otherPays: {
  //             ...currentPayrollEditing?.otherPays,
  //             morePay: Array.isArray(currentPayrollEditing?.otherPays?.morePay)
  //               ? currentPayrollEditing?.otherPays?.morePay.map((item) => ({
  //                   ...item,
  //                   amount: Number(item.amount), // Ensure it's a number
  //                 }))
  //               : [], // Default to an empty array if morePay isn't an array
  //           },
  //           unpaidAdjustments: UnpaidAdjustmentPayload,
  //           amounts: {
  //             // gross: currentPayrollEditing?.totalTimesheetPayAmount,
  //             netPay: calculateTotalAmount(currentPayrollEditing),
  //             diff: currentPayrollEditing?.difference
  //               ? currentPayrollEditing?.difference
  //               : 0,
  //             insurance: currentPayrollEditing?.deductions
  //               ?.filter((ded) => ded.deductionType == 2)
  //               .reduce((acc, ded) => acc + ded.deducted, 0),
  //           },
  //           comments: currentPayrollEditing?.comments,
  //         },
  //       },
  //     ];
  //     // console.log("createdPayload", createdPayload);
  //     // return;
  //     dispatch(
  //       updatePayrollStart({
  //         uiid: currentPayrollEditing?.payrollId,
  //         coll: "Payroll",
  //         payload: createdPayload,
  //         ...(UnpaidAdjustmentPayload?.length > 0 && {
  //           preActions: ["UpdateUnpaidTs"],
  //         }),

  //         onSuccess: async () => {
  //           if (dataDeduction.length) {
  //             if (dataDeduction.length > 0) {
  //               for (const item of dataDeduction) {
  //                 await updateDeductionsData(item); // One-by-one, sequential
  //               }
  //               // Finally, after all updates are done, call onClose()
  //               onHoldClick(); // Assuming onClose is a function that you want to call at the end
  //             }
  //           } else {
  //             onHoldClick();
  //           }
  //         },
  //       }),
  //     );
  //   } catch (error) {
  //     console.log("Error in handleSave Paystub:", error);
  //   }
  // };
  //13-12-2025 safe version
  const handleSave = async (status) => {
    try {
      if (status === 2 && !calculateTotalAmount(currentPayrollEditing)) {
        showToast("Cannot Finalize without any amount", "info", 1000);
        return;
      }
      //////Adjustments to change overflow and underflow logic for the deductions and
      ///// update the deduction document for installments for the adjusted values for installments
      // Helper function to round to 2 decimal places
      const round = (n) => Number(Number(n || 0).toFixed(2));

      // Step 1: Identify removed deductions (in oldDeductions but not in new deductions)
      const removedDeductions = [];
      const newDeductionMap = new Map();

      // Create a map of current deductions by uiid and installmentId
      currentPayrollEditing?.deductions?.forEach((item) => {
        const key = `${item?.uiid}_${item?._id}`;
        newDeductionMap.set(key, {
          uiid: item?.uiid,
          amount: Number(item?.deductionAmountLelf || 0),
          installmentId: item?._id,
        });
      });

      // Find deductions that were removed or changed
      currentPayrollEditing?.oldDeductions?.forEach((oldItem) => {
        const key = `${oldItem?.uiid}_${oldItem?._id}`;
        const oldAmount = Number(oldItem?.deductionAmountLelf || 0);

        if (oldAmount > 0 && !newDeductionMap.has(key)) {
          // This deduction was removed, needs to be reverted
          removedDeductions.push({
            uiid: oldItem?.uiid,
            amount: oldAmount,
            installmentId: oldItem?._id,
          });
        }
      });

      // Step 2: Build adjustments array for new/updated deductions
      let adjustments = [];
      currentPayrollEditing?.deductions?.forEach((item) => {
        const enteredAmount = Number(item?.deductionAmountLelf || 0);
        if (enteredAmount > 0) {
          adjustments.push({
            uiid: item?.uiid,
            amount: enteredAmount,
            installmentId: item?._id,
          });
        }
      });

      // Step 3: Process reverts FIRST, then apply new adjustments
      if (removedDeductions.length > 0 || adjustments.length > 0) {
        try {
          const revertResults = [];
          const adjustResults = [];

          // PHASE 1: Revert removed deductions SEQUENTIALLY
          for (const adj of removedDeductions) {
            // console.log(
            //   `🔄 Reverting deduction for uiid: ${adj.uiid}, installment: ${adj.installmentId}`,
            // );

            // Fetch the current deduction document
            const res = await axiosRequest({
              method: "GET",
              url: ENDPOINTS.EMPLOYEE_DEDUCTIONS.GET_BY_UIID,
              params: {
                coll: "EmpDeduction",
                fil: JSON.stringify({
                  uiid: { type: "NONE", value: adj.uiid },
                }),
              },
            });

            const deductionDoc = res?.data?.data;
            if (!deductionDoc) {
              throw new Error(`Deduction not found for uiid ${adj.uiid}`);
            }

            // Get the total deducted amount and calculate per-installment amount
            const totalDeducted = round(
              Number(deductionDoc?.amounts?.deducted || 0),
            );
            const numInstallments = (deductionDoc.installments || []).length;
            const perInstallmentAmount = round(totalDeducted / numInstallments);

            // console.log(
            //   `📊 Total Deducted: ${totalDeducted}, Installments: ${numInstallments}, Per Installment: ${perInstallmentAmount}`,
            // );

            let installments = (deductionDoc.installments || []).map((i) => ({
              ...i,
              receivable: round(Math.max(0, Number(i?.receivable || 0))),
              received: round(Math.max(0, Number(i?.received || 0))),
            }));

            const currentIndex = installments.findIndex(
              (i) => i._id?.toString() === adj.installmentId?.toString(),
            );

            if (currentIndex !== -1) {
              const inst = installments[currentIndex];
              const currentReceived = inst.received;
              const amountToRevert = currentReceived; // Amount that needs to be reverted

              // console.log(
              //   `📊 Current installment - Received: ${currentReceived}, Receivable: ${inst.receivable}`,
              // );
              // console.log(`🔄 Amount to revert: ${amountToRevert}`);

              // Reset current installment to original equal split
              inst.payrollId = null;
              inst.received = 0;
              inst.receivable = perInstallmentAmount;

              // Now distribute the reverted amount back to subsequent installments
              let amountToDistribute = amountToRevert - perInstallmentAmount; // Excess that was taken from others

              // console.log(
              //   `📤 Amount to distribute back: ${amountToDistribute}`,
              // );

              if (amountToDistribute > 0) {
                // This installment had overflow, return it to subsequent installments
                for (
                  let i = currentIndex + 1;
                  i < installments.length && amountToDistribute > 0;
                  i++
                ) {
                  const nxt = installments[i];
                  const originalAmount = perInstallmentAmount;
                  const currentTotal = nxt.received + nxt.receivable;
                  const deficit = originalAmount - currentTotal; // How much this installment is missing

                  if (deficit > 0) {
                    const amountToRestore = Math.min(
                      deficit,
                      amountToDistribute,
                    );
                    console.log(
                      `  ↪️ Installment ${i}: Restoring ${amountToRestore} to receivable (deficit: ${deficit})`,
                    );
                    nxt.receivable = round(nxt.receivable + amountToRestore);
                    amountToDistribute = round(
                      amountToDistribute - amountToRestore,
                    );
                  }
                }
              } else if (amountToDistribute < 0) {
                // This installment had underflow, it needs to take from subsequent installments
                let amountNeeded = Math.abs(amountToDistribute);
                for (
                  let i = currentIndex + 1;
                  i < installments.length && amountNeeded > 0;
                  i++
                ) {
                  const nxt = installments[i];
                  const canTake = Math.min(nxt.receivable - 0, amountNeeded); // Take only from receivable
                  if (canTake > 0) {
                    // console.log(
                    //   `  ↩️ Installment ${i}: Taking ${canTake} from receivable`,
                    // );
                    nxt.receivable = round(nxt.receivable - canTake);
                    amountNeeded = round(amountNeeded - canTake);
                  }
                }
              }

              // console.log(
              //   `✅ Reverted installment - Received: ${inst.received}, Receivable: ${inst.receivable}`,
              // );
            }

            // Calculate total received after revert
            const totalReceivedAfter = round(
              installments.reduce((sum, i) => sum + i.received, 0),
            );

            // console.log(
            //   `💰 Total received after revert: ${totalReceivedAfter}`,
            // );

            // Verify total consistency
            const totalReceivableAfter = round(
              installments.reduce((sum, i) => sum + i.receivable, 0),
            );
            const grandTotal = round(totalReceivedAfter + totalReceivableAfter);
            // console.log(
            //   `🔍 Verification - Received: ${totalReceivedAfter}, Receivable: ${totalReceivableAfter}, Total: ${grandTotal}, Expected: ${totalDeducted}`,
            // );

            // Prepare update payload
            const updatePayload = {
              payload: [
                {
                  type: "SET",
                  value: {
                    _skipFlatten: ["installments"],
                    installments: installments.map((it) => ({
                      ...it,
                      received: round(it.received),
                      receivable: round(it.receivable),
                    })),
                    amounts: {
                      ...deductionDoc.amounts,
                      received: totalReceivedAfter,
                    },
                  },
                },
              ],
            };

            // console.log("performing paytch two");
            // Update the deduction document
            const updateResponse = await axiosRequest({
              method: "PATCH",
              url: ENDPOINTS.EMPLOYEE_DEDUCTIONS.UPDATE,
              params: { coll: "EmpDeduction", uiid: adj.uiid },
              data: updatePayload,
            });
            // console.log({ updateResponse });
            if (!updateResponse?.success) {
              throw new Error("Updating Deductions Failed");
            }

            revertResults.push({
              uiid: adj.uiid,
              installmentId: adj.installmentId,
              action: "reverted",
              updatedDoc: updateResponse?.data || deductionDoc,
            });
          }

          if (revertResults.length > 0) {
            // console.log("✅ Reverted deductions:", revertResults);
          }

          // PHASE 2: Apply new adjustments SEQUENTIALLY
          for (const adj of adjustments) {
            // console.log(
            //   `➕ Applying adjustment for uiid: ${adj.uiid}, amount: ${adj.amount}`,
            // );

            const res = await axiosRequest({
              method: "GET",
              url: ENDPOINTS.EMPLOYEE_DEDUCTIONS.GET_BY_UIID,
              params: {
                coll: "EmpDeduction",
                fil: JSON.stringify({
                  uiid: { type: "NONE", value: adj.uiid },
                }),
              },
            });
            const deductionDoc = res?.data?.data;
            if (!deductionDoc) {
              throw new Error(`Deduction not found for uiid ${adj.uiid}`);
            }

            const desiredTotal = round(Number(adj.amount || 0));

            let installments = (deductionDoc.installments || []).map((i) => ({
              ...i,
              receivable: round(Math.max(0, Number(i?.receivable || 0))),
              received: round(Math.max(0, Number(i?.received || 0))),
            }));

            const currentIndex = installments.findIndex(
              (i) => i._id?.toString() === adj.installmentId?.toString(),
            );

            if (currentIndex !== -1) {
              const inst = installments[currentIndex];
              const oldTotal = inst.received + inst.receivable;

              // console.log(
              //   `📊 Before adjustment - Received: ${inst.received}, Receivable: ${inst.receivable}, Total: ${oldTotal}`,
              // );

              // Set current installment directly to entered amount
              inst.payrollId = currentPayrollEditing?.payrollId || null;
              inst.received = desiredTotal;
              inst.receivable = 0;

              let diff = desiredTotal - oldTotal;

              // console.log(`🔄 Difference: ${diff}`);

              if (diff > 0) {
                // Overflow → take from subsequent installments' receivable
                // console.log(
                //   `⬇️ Overflow: Taking ${diff} from subsequent installments`,
                // );
                for (
                  let i = currentIndex + 1;
                  i < installments.length && diff > 0;
                  i++
                ) {
                  let nxt = installments[i];
                  let reduceHere = Math.min(nxt.receivable, diff);
                  // console.log(
                  //   `  ↪️ Installment ${i}: Reducing receivable by ${reduceHere} (was ${nxt.receivable})`,
                  // );
                  nxt.receivable = round(nxt.receivable - reduceHere);
                  diff = round(diff - reduceHere);
                }

                if (diff > 0) {
                  console.warn(
                    `⚠️ Warning: Could not fully distribute overflow. Remaining: ${diff}`,
                  );
                  throw new Error(
                    "Warning: Could not fully distribute overflow logic for Deductions",
                  );
                }
              } else if (diff < 0) {
                // Underflow → add back to subsequent installments' receivable
                let rollback = Math.abs(diff);
                // console.log(
                //   `⬆️ Underflow: Adding back ${rollback} to subsequent installments`,
                // );
                for (
                  let i = currentIndex + 1;
                  i < installments.length && rollback > 0;
                  i++
                ) {
                  // console.log(
                  //   `  ↩️ Installment ${i}: Adding ${rollback} to receivable (was ${installments[i].receivable})`,
                  // );
                  installments[i].receivable = round(
                    installments[i].receivable + rollback,
                  );
                  rollback = 0;
                }
              }

              // console.log(
              //   `✅ After adjustment - Received: ${inst.received}, Receivable: ${inst.receivable}`,
              // );
            }

            const totalReceivedAfter = round(
              installments.reduce((sum, i) => sum + i.received, 0),
            );

            // console.log(
            //   `💰 Total received after adjustment: ${totalReceivedAfter}`,
            // );

            // Verify total consistency
            const totalReceivableAfter = round(
              installments.reduce((sum, i) => sum + i.receivable, 0),
            );
            const totalDeducted = round(
              Number(deductionDoc?.amounts?.deducted || 0),
            );
            const grandTotal = round(totalReceivedAfter + totalReceivableAfter);
            console.log(
              `🔍 Verification - Received: ${totalReceivedAfter}, Receivable: ${totalReceivableAfter}, Total: ${grandTotal}, Expected: ${totalDeducted}`,
            );

            const updatePayload = {
              payload: [
                {
                  type: "SET",
                  value: {
                    _skipFlatten: ["installments"],
                    installments: installments.map((it) => ({
                      ...it,
                      received: round(it.received),
                      receivable: round(it.receivable),
                    })),
                    amounts: {
                      ...deductionDoc.amounts,
                      received: totalReceivedAfter,
                    },
                  },
                },
              ],
            };
            const updateResponse = await axiosRequest({
              method: "PATCH",
              url: ENDPOINTS.EMPLOYEE_DEDUCTIONS.UPDATE,
              params: { coll: "EmpDeduction", uiid: adj.uiid },
              data: updatePayload,
            });

            adjustResults.push({
              uiid: adj.uiid,
              installmentId: adj.installmentId,
              action: "applied",
              updatedDoc: updateResponse?.data || deductionDoc,
            });
          }

          if (adjustResults.length > 0) {
            // console.log("✅ Applied adjustments:", adjustResults);
          }

          // showToast("All adjustments processed successfully", "success");
        } catch (error) {
          console.error(error);
          const message =
            error instanceof Error ? error.message : "Something went wrong";
          showToast(message, "error");
          return;
        }
      }

      /////////////////////////////////////////////

      const deductionsResult =
        groupDeductions(currentPayrollEditing?.deductions ?? []) || {};

      // const dataDeduction = generateInstallmentUpdatePayloads(
      //   currentPayrollEditing?.deductions,
      //   currentPayrollEditing?.payrollId,
      // );

      const UnpaidAdjustmentPayload =
        currentPayrollEditing?.unpaidtimesheets?.map((item) => ({
          timesheetId: item?.uiid,
          deduction: item?.amounts?.deduction || 0,
        })) || [];
      
      const collectedTsWithAmounts = (currentPayrollEditing?.unpaidtimesheets || []).concat(currentPayrollEditing?.timesheets || [])?.map((item) => ({
        id: item?.uiid,
        value: Number(item?.amounts?.deduction).toFixed(2) || 0,
      }));
      let createdPayload = [
        {
          type: "SET",
          value: {
            _skipFlatten: [
              "timesheets.ids",
              "timesheets.unpaidIds",
              "timesheets.eachAmts",
              "otherPays.morePay",
              "deductions.insurance.installmentId",
              "deductions.regular",
              "bonuses.ids",
              "adjustments",
            ],
            timesheets: {
              totalHrs: currentPayrollEditing?.totalTimesheetHours || 0,
              totalPay: currentPayrollEditing?.totalTimesheetPayAmount || 0,
              // ids: allTimesheetIds?.map((data) => data.uiid) || [],
              ids:
                currentPayrollEditing?.timesheets?.map((data) => data.uiid) ||
                [],
              // currentTSIDs: currentPayrollEditing?.timesheets?.map((data) => data.uiid) || [],
              unpaidIds:
                currentPayrollEditing?.unpaidtimesheets?.map(
                  (data) => data.uiid,
                ) || [],
              //temporaray
              ...(currentPayrollEditing?.modifications?.type && {
                modifications: {
                  type: currentPayrollEditing?.modifications?.type,
                  amt: currentPayrollEditing?.modifications?.amt,
                },
              }),
              totalUnpaid: currentPayrollEditing?.modifications?.amt
                ? currentPayrollEditing?.modifications?.amt
                : currentPayrollEditing?.modifications?.amt === 0
                  ? 0
                  : currentPayrollEditing?.totalUnpaidPayAmount || 0,
              eachAmts:collectedTsWithAmounts
            },
            bonuses: {
              totalPay: currentPayrollEditing?.totalBonuses || 0,
              ids: currentPayrollEditing?.bonusIds || [],
            },
            status,
            adjustments: {
              short: {
                ids: currentPayrollEditing?.shortIds || [],
                amt: currentPayrollEditing?.totalShort || 0,
              },
              excess: {
                ids: currentPayrollEditing?.excessIds || [],
                amt: currentPayrollEditing?.totalExcess || 0,
              },
            },
            deductions: deductionsResult,

            ...(status === 2
              ? {
                  finalized: {
                    by: loginUserUiid,
                    on: new Date().toISOString(),
                  },
                }
              : {}),
            otherPays: {
              ...currentPayrollEditing?.otherPays,
              morePay: Array.isArray(currentPayrollEditing?.otherPays?.morePay)
                ? currentPayrollEditing?.otherPays?.morePay.map((item) => ({
                    ...item,
                    amount: Number(item.amount), // Ensure it's a number
                  }))
                : [], // Default to an empty array if morePay isn't an array
            },
            unpaidAdjustments: UnpaidAdjustmentPayload,
            amounts: {
              // gross: currentPayrollEditing?.totalTimesheetPayAmount,
              netPay: calculateTotalAmount(currentPayrollEditing),
              diff: currentPayrollEditing?.difference
                ? currentPayrollEditing?.difference
                : 0,
              insurance: currentPayrollEditing?.deductions
                ?.filter((ded) => ded.deductionType == 2)
                .reduce((acc, ded) => acc + ded.deducted, 0),
            },
            comments: currentPayrollEditing?.comments,
          },
        },
      ];
      // console.log("createdPayload", createdPayload);
      // return;
      dispatch(
        updatePayrollStart({
          uiid: currentPayrollEditing?.payrollId,
          coll: "Payroll",
          payload: createdPayload,
          ...(UnpaidAdjustmentPayload?.length > 0 && {
            preActions: ["UpdateUnpaidTs"],
          }),

          onSuccess: async () => {
            onHoldClick();
          },
        }),
      );
    } catch (error) {
      console.log("Error in handleSave Paystub:", error);
    }
  };

  return (
    <div className="">
      <div className="px-8">
        {earnings?.length > 0 || deductions?.length > 0 ? (
          <div className="grid grid-cols-2 gap-4 max-w-7xl ">
            <div>
              <h2 className="text-lg font-semibold text-neutrals-900 ">
                EARNINGS
              </h2>
              <div className="max-h-[70vh] overflow-auto">
                {/* {earningsSections.map((section, idx) => ( */}
                {earnings?.map((section, idx) => (
                  <Section key={idx} {...section} />
                ))}
              </div>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-neutrals-900 ">
                DEDUCTIONS
              </h2>
              <div className="max-h-[70vh] overflow-auto">
                {/* {deductionSections.map((section, idx) => ( */}
                {deductions.map((section, idx) => (
                  <Section key={idx} {...section} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center">
            <h4>
            Nothing To Display here..
            </h4>
          </div>
        )}
      </div>
      {!hideButtons && (
        <div className="flex justify-end gap-2 mt-2 p-4">
          <Button
            title="Hold"
            variant="outlined"
            onClick={() => handleSave(3)}
          />
          <Button title="Save" onClick={() => handleSave(1)} />
          {/* {showFinalize && <Button title="Finalize" onClick={()=>handleSave(2)} />} */}
          <Button title="Finalize" onClick={() => handleSave(2)} />
        </div>
      )}
    </div>
  );
};

export default PayStub;
